import React, { useEffect , useState } from 'react';
import { Lock, Eye, EyeOff, Shield, CheckCircle, AlertCircle, KeyRound, Mail } from 'lucide-react';
import './ResetPassword.css';

const API_URL = process.env.REACT_APP_LOGIN_APP_API_BASE;

const ResetPassword = ({ onSuccess }) => {
  const [step, setStep] = useState(1); // 1 = verify OTP, 2 = reset password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    const { password, confirmPassword } = formData;
    if (!password || !confirmPassword) return 'Please fill in all fields';
    if (password.length < 6) return 'Password must be at least 6 characters long';
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  };

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const emailFromQuery = params.get('email');
  if (emailFromQuery) {
    setEmail(emailFromQuery);
  }
}, []);


  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const validationError = validatePassword();
      if (validationError) throw new Error(validationError);

      const res = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, password: formData.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      setSuccess(data.message);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        else window.location.href = '/';
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const { password } = formData;
    if (!password) return { level: 0, text: '' };

    let strength = 0;
    let feedback = [];
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (password.length < 6) feedback.push('At least 6 characters');
    if (!/[A-Z]/.test(password)) feedback.push('One uppercase letter');
    if (!/[0-9]/.test(password)) feedback.push('One number');

    let level = 'weak', color = '#ef4444';
    if (strength >= 4) [level, color] = ['strong', '#22c55e'];
    else if (strength >= 2) [level, color] = ['medium', '#f59e0b'];

    return {
      level: strength,
      text: level,
      color,
      feedback: feedback.length > 0 ? `Missing: ${feedback.join(', ')}` : 'Good password!',
    };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="reset-container">
      <div className="reset-card">
        <div className="reset-header">
          <div className="icon-container"><Shield size={40} color="white" /></div>
          <h1 className="reset-title">Admin Password Reset</h1>
          <p className="reset-subtitle">Reset your password securely using OTP</p>
        </div>

        {error && <div className="error-message"><AlertCircle size={16} /> {error}</div>}
        {success && <div className="success-message"><CheckCircle size={16} /> {success}</div>}

        {step === 1 && (
          <form onSubmit={handleOtpVerification} className="reset-form">
            <div className="input-group">
              <label htmlFor="email">Email</label>
              <div className="input-container">
                <Mail size={20} className="input-icon" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="otp">OTP</label>
              <div className="input-container">
                <KeyRound size={20} className="input-icon" />
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="input-field"
                  placeholder="Enter OTP"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button type="submit" className="submit-button" disabled={loading || !email || !otp}>
              {loading ? <>Verifying...</> : <>Verify OTP</>}
            </button>
          </form>
        )}

        {step === 2 && !success && (
          <form onSubmit={handleReset} className="reset-form">
            <div className="input-group">
              <label htmlFor="password">New Password</label>
              <div className="input-container">
                <Lock size={20} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-field"
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {formData.password && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{ width: `${(passwordStrength.level / 6) * 100}%`, backgroundColor: passwordStrength.color }}
                    />
                  </div>
                  <div className="strength-text" style={{ color: passwordStrength.color }}>
                    Password strength: {passwordStrength.text}
                  </div>
                  <div className="strength-feedback">{passwordStrength.feedback}</div>
                </div>
              )}
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-container">
                <Lock size={20} className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`input-field ${
                    formData.confirmPassword && formData.confirmPassword !== formData.password ? 'input-error' : ''
                  }`}
                  placeholder="Confirm password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {formData.confirmPassword && formData.confirmPassword !== formData.password && (
                <div className="field-error">Passwords do not match</div>
              )}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading || !formData.password || !formData.confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
